import React, { useMemo, useState }  from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import SystemStatusTable from "../elements/SystemStatusTable";
import {IconClock, IconHistory, IconWorld} from "@tabler/icons-react";

// ---------- i18n ----------
const dict = {
    en: {
        zoom: "Zoom",
        zoomDay: "Day",
        zoomWeek: "Week",
        zoomMonth: "Month",

        chart: {
            responseTime: "Response Time (s)",
            successTime: "Success time",
            loadError: "Load error",
            tooltipDateFmt: "%Y-%m-%d %H:%M:%S",
            tooltipSuccess: "Success time",
            tooltipError: "Load error",
            noData: "No monitoring data yet",
        },

        cards: {
            lastUpdate: "Last Update",
            nextUpdate: "Next Update",
            currentUptime: "Current Day Uptime",
        },
    },

    ru: {
        zoom: "Масштаб",
        zoomDay: "День",
        zoomWeek: "Неделя",
        zoomMonth: "Месяц",

        chart: {
            responseTime: "Время ответа (с)",
            successTime: "Успешное время",
            loadError: "Ошибка загрузки",
            tooltipDateFmt: "%Y-%m-%d %H:%M:%S",
            tooltipSuccess: "Успешное время",
            tooltipError: "Ошибка загрузки",
            noData: "Мониторинговых данных пока нет",
        },

        cards: {
            lastUpdate: "Последнее обновление",
            nextUpdate: "Следующее обновление",
            currentUptime: "Аптайм за сегодня",
        },
    },

    ua: {
        zoom: "Масштаб",
        zoomDay: "День",
        zoomWeek: "Тиждень",
        zoomMonth: "Місяць",

        chart: {
            responseTime: "Час відповіді (с)",
            successTime: "Успішний час",
            loadError: "Помилка завантаження",
            tooltipDateFmt: "%Y-%m-%d %H:%M:%S",
            tooltipSuccess: "Успішний час",
            tooltipError: "Помилка завантаження",
            noData: "Даних моніторингу ще немає",
        },

        cards: {
            lastUpdate: "Останнє оновлення",
            nextUpdate: "Наступне оновлення",
            currentUptime: "Аптайм за день",
        },
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- helpers ----------
function generateRandomData(points, min, max, stepMs = 3600000) {
    const now = Date.now();
    return Array.from({ length: points }).map((_, i) => [
        now - i * stepMs,
        Math.random() * (max - min) + min,
    ]).reverse();
}

// ---------- components ----------
const UptimeChart = ({ t, statuses }) => {
    const [zoom, setZoom] = useState("Day");

    const successPoints = [];
    const errorPoints = [];
    statuses.forEach((s) => {
        const ts = new Date(s.created).getTime();
        if (Number.isNaN(ts)) return;
        const timeSeconds = Number(s.response_time) ? Number(s.response_time) / 1000 : 0;
        if (String(s.staus) === "1") {
            successPoints.push([ts, timeSeconds]);
        } else {
            errorPoints.push([ts, timeSeconds]);
        }
    });

    const dataSuccess = successPoints;
    const dataError = errorPoints;

    const chartOptions = {
        chart: { type: "line", zoomType: "x" },
        title: { text: "" },
        xAxis: { type: "datetime" },
        yAxis: { title: { text: t.chart.responseTime } },
        legend: { layout: "horizontal", align: "center", verticalAlign: "bottom" },
        tooltip: {
            shared: true,
            formatter: function () {
                return `<b>${Highcharts.dateFormat("${t.chart.tooltipDateFmt}", this.x)}</b><br/>
          <span style="color:${this.points[0]?.color}">●</span> ${t.chart.tooltipSuccess}: ${this.points[0]?.y?.toFixed(2) || 0}s<br/>
          <span style="color:${this.points[1]?.color}">●</span> ${t.chart.tooltipError}: ${this.points[1]?.y?.toFixed(2) || 0}s`;
            },
        },
        series: [
            { name: t.chart.successTime, data: dataSuccess, color: "#1f78b4" },
            { name: t.chart.loadError,   data: dataError,   color: "#e31a1c" },
        ],
    };

    const zoomButtons = [
        { key: "Day",   label: t.zoomDay },
        { key: "Week",  label: t.zoomWeek },
        { key: "Month", label: t.zoomMonth },
    ];

    if (!dataSuccess.length && !dataError.length) {
        return <div className="border rounded-4 p-3 bg-white shadow-sm">{t.chart.noData || "No monitoring data yet"}</div>;
    }

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ marginBottom: "10px" }}>
                <span>{t.zoom}: </span>
                {zoomButtons.map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setZoom(key)}
                        style={{
                            padding: "5px 10px",
                            margin: "0 5px",
                            backgroundColor: zoom === key ? "#3f51b5" : "#f4f4f4",
                            color: zoom === key ? "white" : "black",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
    );
};

const Dashboard = ({ lang: langProp, singleArgs }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const details = singleArgs?.details || {};
    const statuses = singleArgs?.statuses || [];
    const countdown = singleArgs?.countdown ?? null;
    const nextUpdateProp = singleArgs?.nextUpdate ? new Date(singleArgs.nextUpdate) : null;

    const heroStyle = {
        background:
            "linear-gradient(120deg, #f3f4f6 0%, #e0f2fe 45%, #dbeafe 100%), radial-gradient(circle at 18% 15%, rgba(255,255,255,0.85), transparent 35%), radial-gradient(circle at 82% 22%, rgba(255,255,255,0.55), transparent 35%)",
        color: "#0f172a",
        boxShadow: "0 12px 32px rgba(59,130,246,0.12)",
        border: "1px solid rgba(59,130,246,0.12)",
    };

    const formatDate = (date) => {
        if (!date) return "—";
        if (typeof date === "string" && date.includes("1970")) return "—";
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return "—";
        // treat epoch-like placeholders as empty
        if (d.getTime() <= 24 * 60 * 60 * 1000 || d.getFullYear() <= 1971) return "—";
        return d.toLocaleString();
    };

    const lastUpdate = details.last_update || details.validate_at || details.created_date;
    const loadIntervalMinutes = (() => {
        const raw = details.load_interval;
        if (raw === undefined || raw === null) return null;
        if (typeof raw === "string" && raw.startsWith("*/")) {
            const val = Number(raw.replace("*/", ""));
            return Number.isNaN(val) ? null : val;
        }
        const val = Number(raw);
        return Number.isNaN(val) ? null : val;
    })();
    const intervalMinutes = loadIntervalMinutes || 1; // default to 1 minute if missing
    const computedNextUpdate = (() => {
        if (!intervalMinutes || Number.isNaN(intervalMinutes) || intervalMinutes <= 0) return null;
        const intervalMs = intervalMinutes * 60 * 1000;
        const now = Date.now();
        const nextAligned = Math.ceil(now / intervalMs) * intervalMs;
        return new Date(nextAligned);
    })();
    const nextUpdate = nextUpdateProp || computedNextUpdate;

    const showCountdown = countdown !== null;

    const systemItems = [
        { name: t.cards.lastUpdate,   value: formatDate(lastUpdate), icon: <IconClock  size={30} color="var(--bs-info)" /> },
        { name: t.cards.nextUpdate,   value: formatDate(nextUpdate), icon: <IconHistory size={30} color="var(--bs-info)" /> },
        { name: t.cards.currentUptime,value: details.today_uptime ? `${details.today_uptime}%` : "—", icon: <IconHistory size={30} color="var(--bs-info)" /> },
    ];

    return (
        <div className="d-flex flex-column gap-4">
            <div className="rounded-4 p-4 shadow-sm" style={heroStyle}>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                        <div className="text-uppercase fw-semibold small text-muted">{t.chart.responseTime}</div>
                        <h2 className="mb-2" style={{ textShadow: "0 3px 10px rgba(15,23,42,0.15)" }}>
                            {details.name || details.url || t.chart.successTime}
                        </h2>
                        {details.url && (
                            <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-white text-dark fw-semibold shadow-sm">
                                <IconWorld size={18} />
                                <span>{details.url}</span>
                            </div>
                        )}
                    </div>
                    <div className="text-end">
                        <div className="text-uppercase fw-semibold small opacity-75">{t.cards.lastUpdate}</div>
                        <div className="fs-4 fw-bold">{formatDate(lastUpdate)}</div>
                        <div className="small opacity-75">{t.cards.nextUpdate}: {formatDate(nextUpdate)}</div>
                    </div>
                </div>
            </div>

            <SystemStatusTable systemItems={systemItems} softwareItems={[]} />
            <div className="d-flex align-items-center justify-content-end text-muted small">
                {t.cards.nextUpdate}: <span className="ms-2 fw-semibold text-dark">{formatDate(nextUpdate)}</span>
                {showCountdown && countdown !== null && (
                    <span className="badge bg-light text-dark ms-3">{String(countdown).padStart(2, "0")}s</span>
                )}
            </div>
            <UptimeChart t={t} statuses={statuses} />
        </div>
    );
};

export default Dashboard;
